import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Server, Network, Cloud, Video, Laptop, ShieldAlert,
  X, CheckCircle, ArrowRight, ShieldCheck, Cpu
} from 'lucide-react';

const servicesData = [
  {
    id: 'server',
    icon: Server,
    color: 'from-cyan-400 to-blue-500',
    title: 'Server Solutions',
    shortDesc: 'Design, configuration, deployment, and optimization of enterprise-grade server infrastructure.',
    fullDesc: 'We provide end-to-end server integration, including Active Directory setups, domain management, RAID configurations, storage networks (SAN/NAS), and server virtualization (Hyper-V / VMware) to maximize efficiency and reliability.',
    features: [
      'Active Directory & LDAP Management',
      'Virtualization & Hypervisor Setup',
      'RAID & Storage Array Configurations (SAN/NAS)',
      'Backup & Disaster Recovery Integration',
      'Hardware Diagnostics & Maintenance SLAs'
    ],
    brands: ['HPE ProLiant', 'Dell PowerEdge', 'Lenovo ThinkSystem', 'Cisco UCS']
  },
  {
    id: 'networking',
    icon: Network,
    color: 'from-blue-500 to-indigo-500',
    title: 'Enterprise Networking',
    shortDesc: 'Highly secure, high-throughput LAN, WAN, and wireless network architectures for modern corporate headquarters.',
    fullDesc: 'Advance IT builds physical and logical network designs featuring structured cat6/fiber cabling, core routing setups, switching hierarchies, VLAN segmentations, and high-performance managed Wi-Fi setups.',
    features: [
      'Core Routing & Managed Switching',
      'Structured Cat6 & Fiber Cabling',
      'VLAN Segregations & WLAN Design',
      'SD-WAN & Multi-Office Site-to-Site VPNs',
      'Real-time Network Traffic Monitoring'
    ],
    brands: ['Cisco', 'Juniper Networks', 'Aruba', 'Ubiquiti UniFi', 'D-Link']
  },
  {
    id: 'cloud',
    icon: Cloud,
    color: 'from-brand-cyan to-indigo-400',
    title: 'Cloud Services',
    shortDesc: 'Scalable migration, hosting, security, and continuous management on leading cloud hyperscalers.',
    fullDesc: 'We help businesses transition smoothly from legacy physical boxes to fully scalable, cost-optimized environments in public, private, or hybrid clouds, with robust serverless and virtual machine instances.',
    features: [
      'Cloud Migration & Architecture Audits',
      'Hybrid Cloud Orchestration',
      'Hosted Exchange & Microsoft 365 Migrations',
      'Off-site Automated Backups & Archival',
      'DevOps Pipelines & Serverless Configurations'
    ],
    brands: ['AWS', 'Microsoft Azure', 'Google Cloud', 'Cloudflare']
  },
  {
    id: 'cctv',
    icon: Video,
    color: 'from-purple-500 to-pink-500',
    title: 'CCTV Surveillance',
    shortDesc: 'Advanced HD IP surveillance cameras, central recording, and analytics for full facility protection.',
    fullDesc: 'Secure your corporate buildings with state-of-the-art surveillance equipment. Our systems feature artificial intelligence capabilities, facial detection, license plate reading, and secure remote-viewing applications.',
    features: [
      'Ultra-HD 4K IP Camera Deployments',
      'Network Video Recorder (NVR) Setup',
      'Remote Secure Live-Feed Access',
      'AI Motion Alerts & Zone Tracking',
      'Redundant Storage Solutions'
    ],
    brands: ['Hikvision', 'Dahua', 'CP Plus', 'Axis Communications']
  },
  {
    id: 'rentals',
    icon: Laptop,
    color: 'from-brand-cyan to-emerald-400',
    title: 'Laptop & Desktop Rentals',
    shortDesc: 'Flexible, high-spec bulk computing hardware rentals for teams, projects, and enterprise training camps.',
    fullDesc: 'Skip capital expenditure overheads. We offer scalable packages of state-of-the-art laptops, desktops, workstations, and thin clients configured to your corporate specifications and backed by 24/7 priority hardware replacement.',
    features: [
      'Intel Core i5/i7 & Apple Silicon Options',
      'Pre-configured OS, Security, & Apps',
      'Flexible Duration (Daily, Monthly, Yearly)',
      'On-site Setup & Network Integration',
      'Express Swap Hardware Maintenance'
    ],
    brands: ['Lenovo ThinkPad', 'HP EliteBook', 'Dell Latitude', 'Apple MacBook']
  },
  {
    id: 'security',
    icon: ShieldAlert,
    color: 'from-rose-500 to-orange-500',
    title: 'Security',
    shortDesc: 'Proactive defense mechanisms, firewall installations, threat mitigation, and penetration audits.',
    fullDesc: 'Safeguard your proprietary intellectual property. We deploy Unified Threat Management (UTM) firewalls, endpoint security agents, intrusion prevention systems, and run employee security training simulations.',
    features: [
      'Next-Gen Firewall Deployments',
      'Endpoint Detection & Response (EDR)',
      'Vulnerability Assessments & Penetration Testing',
      'Multi-Factor Authentication (MFA) Setup',
      'Compliance Auditing (ISO 27001 / GDPR)'
    ],
    brands: ['Fortinet FortiGate', 'Sophos', 'Symantec', 'Palo Alto Networks']
  }
];

export default function Services() {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="services" className="relative py-24 border-t border-white/5 bg-navy-deep/40">
      <div className="max-w-7xl mx-auto px-6 md:px-8">

        {/* Section Title */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-display font-black text-4xl md:text-5xl text-white tracking-tight mb-4"
          >
            Our Enterprise <span className="text-gradient">Services</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-sans text-lg text-slate-400"
          >
            Transforming corporate environments since 1999 with robust, intelligent, and secure technological architectures.
          </motion.p>
        </div>

        {/* Grid of Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service, idx) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group relative rounded-2xl glass-panel p-8 glass-panel-hover flex flex-col justify-between"
              >
                {/* Neon glow effect on hover */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />

                <div>
                  {/* Icon Block */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${service.color} p-[1px] mb-6 flex items-center justify-center`}>
                    <div className="w-full h-full rounded-xl bg-navy-dark flex items-center justify-center text-white group-hover:bg-transparent transition-colors duration-300">
                      <Icon size={20} className="group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  </div>

                  <h3 className="font-display font-bold text-xl text-white mb-3 group-hover:text-brand-cyan transition-colors duration-300">
                    {service.title}
                  </h3>

                  <p className="font-sans text-sm text-slate-400 leading-relaxed mb-8">
                    {service.shortDesc}
                  </p>
                </div>

                <button
                  onClick={() => setSelectedService(service)}
                  className="flex items-center gap-1.5 text-xs font-bold tracking-wider text-brand-cyan group-hover:text-white transition-colors duration-300 cursor-pointer"
                >
                  <span>LEARN MORE</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Detail Modal Component */}
      <AnimatePresence>
        {selectedService && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

            {/* Modal Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedService(null)}
              className="absolute inset-0 bg-navy-dark/85 backdrop-blur-md"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="relative w-full max-w-2xl rounded-3xl glass-panel p-8 md:p-10 shadow-2xl z-10 overflow-hidden"
            >
              {/* Top gradient strip */}
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${selectedService.color}`} />

              {/* Close Button */}
              <button
                onClick={() => setSelectedService(null)}
                className="absolute top-6 right-6 p-2 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
              >
                <X size={18} />
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${selectedService.color} flex items-center justify-center text-white shadow-lg`}>
                  {React.createElement(selectedService.icon, { size: 22 })}
                </div>
                <div>
                  <h3 className="font-display font-extrabold text-2xl text-white">
                    {selectedService.title}
                  </h3>
                  <span className="font-sans text-[10px] text-brand-cyan tracking-[0.2em] font-bold uppercase">
                    Advance IT Services Capabilities
                  </span>
                </div>
              </div>

              <p className="font-sans text-slate-300 leading-relaxed text-sm mb-6">
                {selectedService.fullDesc}
              </p>

              {/* Core Features */}
              <div className="mb-6">
                <h4 className="font-display font-bold text-sm text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                  <ShieldCheck size={16} className="text-brand-cyan" />
                  Key Infrastructure Features
                </h4>
                <div className="flex flex-col gap-2.5">
                  {selectedService.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-brand-cyan mt-0.5 shrink-0" />
                      <span className="font-sans text-xs text-slate-400 leading-normal">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brands Supported */}
              <div className="border-t border-white/5 pt-6 flex flex-wrap items-center gap-4">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <Cpu size={12} className="text-brand-blue" />
                  Primary Partner Systems:
                </span>
                <div className="flex flex-wrap gap-2">
                  {selectedService.brands.map((brand, i) => (
                    <span key={i} className="px-2.5 py-1 rounded bg-white/5 border border-white/5 font-mono text-[10px] text-slate-400">
                      {brand}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quick Inquiry CTA */}
              <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
                <span className="text-[10px] text-slate-500 font-medium">Need custom configuration or contact details?</span>
                <Link
                  to="/contact"
                  onClick={() => setSelectedService(null)}
                  className="px-6 py-3 w-full sm:w-auto text-center rounded-xl bg-gradient-to-r from-brand-cyan to-brand-blue text-white font-bold text-xs tracking-wider uppercase hover:shadow-lg hover:shadow-brand-cyan/15 hover:translate-y-[-1px] transition-all"
                >
                  Request Configuration Details
                </Link>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
